import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { IMAGE_CONSTANTS } from '@/constants';

interface TaskSearchBarProps {
  searchQuery: string;
  taskFilter: 'all' | 'audit' | 'move';
  onSearchChange: (query: string) => void;
  onFilterChange: (filter: 'all' | 'audit' | 'move') => void;
}

/**
 * Search bar + filter dropdown matching Xamarin SearchBarAndFilter.xaml.
 *
 * Xamarin layout:
 *  - HeightRequest=40, Margin=0, Padding=0
 *  - Grid ColumnDefinitions: Phone="60*,40*", Default="80*,20*"
 *  - Search Frame: bg=white, Padding=0, Margin="9,0,0,0", CornerRadius=3, Border=#D2DAE1
 *    Entry: FontFamily=PoppinsRegular, FontSize=12, Margin="10,0,0,0", Placeholder="Search", PlaceholderColor=#516573
 *  - Filter Frame: bg=white, Padding=0, Margin="0,0,10,0", CornerRadius=3, Border=#D2DAE1
 *    Picker: FontSize=12, TextColor=#516573, Margin="10,0,0,0"
 *    Dropdown arrow: filter_dropdown.svg 8px, #D2DAE1 (phone only)
 */
export function TaskSearchBar({
  searchQuery,
  taskFilter,
  onSearchChange,
  onFilterChange,
}: TaskSearchBarProps) {
  const { t } = useTranslation();
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  const FILTERS: { value: 'all' | 'audit' | 'move'; label: string }[] = [
    { value: 'all', label: t('dashboard.filterAll') },
    { value: 'audit', label: t('dashboard.filterAudit') },
    { value: 'move', label: t('dashboard.filterMove') },
  ];

  const selectedLabel = FILTERS.find((f) => f.value === taskFilter)?.label ?? '';

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(localQuery);
    }, 300);
    return () => {
      clearTimeout(timer);
    };
  }, [localQuery, onSearchChange]);

  // Sync external query changes
  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isFilterOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterOpen]);

  const handleFilterSelect = (value: 'all' | 'audit' | 'move') => {
    onFilterChange(value);
    setIsFilterOpen(false);
  };

  return (
    /* Xamarin: HeightRequest=40, Grid Phone="60*,40*" */
    <div className="flex h-10 gap-2 mx-[10px]">
      {/* Search input — Xamarin: Frame Col=0, CornerRadius=3, Border=#D2DAE1 */}
      <div className="min-w-0 flex-[6] rounded-[3px] border border-searchbar-border bg-white">
        {/* Xamarin: Entry FontFamily=PoppinsRegular, FontSize=12, Margin="10,0,0,0", PlaceholderColor=#516573 */}
        <input
          type="text"
          placeholder={t('dashboard.search')}
          value={localQuery}
          onChange={(e) => {
            setLocalQuery(e.target.value);
          }}
          className="h-full w-full bg-transparent pl-[10px] pr-2 text-xs font-poppins text-task-details placeholder:text-task-label outline-none"
        />
      </div>

      {/* Filter dropdown — Xamarin: Frame Col=1, CornerRadius=3, Border=#D2DAE1, smaller width */}
      <div
        ref={filterRef}
        className="relative min-w-0 flex-[4] rounded-[3px] border border-searchbar-border bg-white"
      >
        {/* Xamarin: Picker FontSize=12, TextColor=#516573, Margin="10,0,0,0" */}
        <button
          type="button"
          onClick={() => {
            setIsFilterOpen((prev) => !prev);
          }}
          className="flex h-full w-full items-center bg-transparent pl-[10px] pr-7 text-xs font-poppins text-task-label outline-none"
        >
          <span className="truncate">{selectedLabel}</span>
        </button>
        {/* Xamarin: filter_dropdown.svg HeightRequest=8, fill=#D2DAE1 */}
        <img
          src={IMAGE_CONSTANTS.filterDropdown}
          alt=""
          className="pointer-events-none absolute right-2 top-1/2 h-2 w-2 -translate-y-1/2"
        />
        {isFilterOpen && (
          <ul className="absolute left-0 right-0 top-full z-50 mt-0.5 rounded-[3px] border border-searchbar-border bg-white py-1 shadow-md">
            {FILTERS.map(({ value, label }) => (
              <li key={value}>
                <button
                  type="button"
                  onClick={() => {
                    handleFilterSelect(value);
                  }}
                  className={`w-full px-[10px] py-1.5 text-left text-xs font-poppins outline-none ${
                    value === taskFilter
                      ? 'bg-[#1357A0] text-white'
                      : 'text-task-label hover:bg-gray-100'
                  }`}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
